import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Alert, Typography, message, Select, Slider } from 'antd';
import { AudioOutlined, VideoCameraOutlined, SettingOutlined, PlayCircleOutlined, PauseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from '../../hooks/useTranslation';

const { Text } = Typography;

// iOS detection utility
const isIPhone = (): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }
    const userAgent = window.navigator.userAgent;
    const isIPhone = /iPhone/i.test(userAgent);
    const isIPad = /iPad/i.test(userAgent);
    const isIPod = /iPod/i.test(userAgent);
    const isIOS = /iOS/i.test(userAgent) ||
        (/Macintosh/i.test(userAgent) && 'ontouchend' in document);
    return isIPhone || isIPad || isIPod || isIOS;
};

interface AudioTestModalProps {
    open: boolean;
    onClose: () => void;
    selectedMicrophone?: string;
    onMicrophoneChange?: (deviceId: string) => void;
    selectedSpeaker?: string;
    onSpeakerChange?: (deviceId: string) => void;
    availableMicrophones?: MediaDeviceInfo[];
    availableSpeakers?: MediaDeviceInfo[];
    onRefreshDevices?: () => void;
}

const AudioTestModal: React.FC<AudioTestModalProps> = ({
    open,
    onClose,
    selectedMicrophone,
    onMicrophoneChange,
    selectedSpeaker,
    onSpeakerChange,
    availableMicrophones = [],
    availableSpeakers = [],
    onRefreshDevices
}) => {
    const { t } = useTranslation();

    // Audio visualization and playback state
    const [isListening, setIsListening] = useState(false);
    const [isPlayingTestAudio, setIsPlayingTestAudio] = useState(false);
    const [audioLevel, setAudioLevel] = useState<number>(0);
    const [audioDeviceError, setAudioDeviceError] = useState<string>('');
    const [isTestingAudio, setIsTestingAudio] = useState(false);
    const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
    const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
    const [dataArray, setDataArray] = useState<Uint8Array | null>(null);
    const [audioStream, setAudioStream] = useState<MediaStream | null>(null);

    // New state for recording and playback
    const [isRecording, setIsRecording] = useState(false);
    const [isRecordingLoading, setIsRecordingLoading] = useState(false);
    const [isPlayingRecording, setIsPlayingRecording] = useState(false);
    const [recordedAudioBlob, setRecordedAudioBlob] = useState<Blob | null>(null);
    const [recordedAudioUrl, setRecordedAudioUrl] = useState<string>('');
    const [recordingDuration, setRecordingDuration] = useState<number>(0);
    const [playbackVolume, setPlaybackVolume] = useState<number>(100);


    // Refs
    const testAudioRef = useRef<HTMLAudioElement | null>(null);
    const audioLevelIntervalRef = useRef<NodeJS.Timeout>();
    const animationFrameRef = useRef<number>();
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const waveformAnimationRef = useRef<number>();
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const recordedChunksRef = useRef<Blob[]>([]);
    const recordingTimerRef = useRef<NodeJS.Timeout>();
    const playbackAudioRef = useRef<HTMLAudioElement | null>(null);

    // Device memory functions
    const saveDeviceSettings = (microphoneId: string, speakerId: string) => {
        localStorage.setItem('audioTest_microphone', microphoneId);
        localStorage.setItem('audioTest_speaker', speakerId);
    };

    const clearInvalidDeviceSettings = () => {
        const savedMic = localStorage.getItem('audioTest_microphone');
        const savedSpeaker = localStorage.getItem('audioTest_speaker');

        // Clear invalid microphone
        if (savedMic && !availableMicrophones.some(mic => mic.deviceId === savedMic)) {
            localStorage.removeItem('audioTest_microphone');
            console.log(t('audioTest.messages.clearedInvalidMicrophone'));
        }

        // Clear invalid speaker
        if (savedSpeaker && !availableSpeakers.some(speaker => speaker.deviceId === savedSpeaker)) {
            localStorage.removeItem('audioTest_speaker');
            console.log(t('audioTest.messages.clearedInvalidSpeaker'));
        }
    };

    const loadDeviceSettings = () => {
        const savedMic = localStorage.getItem('audioTest_microphone');
        const savedSpeaker = localStorage.getItem('audioTest_speaker');

        // Check if saved microphone is still available
        if (savedMic && onMicrophoneChange) {
            const micExists = availableMicrophones.some(mic => mic.deviceId === savedMic);
            if (micExists) {
                onMicrophoneChange(savedMic);
            } else {
                // Clear invalid saved microphone
                localStorage.removeItem('audioTest_microphone');
                console.log('Saved microphone no longer available, using default');
            }
        }

        // Check if saved speaker is still available
        if (savedSpeaker && onSpeakerChange) {
            const speakerExists = availableSpeakers.some(speaker => speaker.deviceId === savedSpeaker);
            if (speakerExists) {
                onSpeakerChange(savedSpeaker);
            } else {
                // Clear invalid saved speaker
                localStorage.removeItem('audioTest_speaker');
                console.log('Saved speaker no longer available, using default');
            }
        }
    };

    // Waveform visualization functions - optimized for real-time monitoring
    const drawWaveform = (analyserNode: AnalyserNode, dataArrayBuffer: Uint8Array) => {
        const canvas = canvasRef.current;

        if (!canvas || !analyserNode || !dataArrayBuffer) return;

        const canvasCtx = canvas.getContext('2d');
        if (!canvasCtx) return;

        const WIDTH = canvas.width;
        const HEIGHT = canvas.height;

        // Clear canvas with dark background
        canvasCtx.fillStyle = '#1a1a1a';
        canvasCtx.fillRect(0, 0, WIDTH, HEIGHT);

        // Get frequency data
        const frequencyData = new Uint8Array(analyserNode.frequencyBinCount);
        analyserNode.getByteFrequencyData(frequencyData);

        // Calculate average volume for level bar (optimized)
        let sum = 0;
        const length = frequencyData.length;
        for (let i = 0; i < length; i += 4) { // Sample every 4th value for performance
            sum += frequencyData[i];
        }
        const average = (sum * 4) / length; // Adjust for sampling
        setAudioLevel(average / 255); // Normalize to 0-1

        // Reduced bar count for better performance during monitoring
        const barCount = 24; // Reduced for smaller canvas
        const barWidth = WIDTH / barCount;

        // Draw blue soundwave bars on dark background
        for (let i = 0; i < barCount; i++) {
            const dataIndex = Math.floor((i / barCount) * length);
            const barHeight = (frequencyData[dataIndex] / 255) * HEIGHT * 0.6; // Scale down for better visual
            const x = i * barWidth;

            // Draw blue bars from center
            if (barHeight > 2) { // Only draw if there's significant audio
                canvasCtx.fillStyle = '#1890ff';
                canvasCtx.fillRect(x + 1, (HEIGHT - barHeight) / 2, barWidth - 2, barHeight);
            }
        }

        // Center line in light blue for reference
        canvasCtx.strokeStyle = 'rgba(24, 144, 255, 0.4)';
        canvasCtx.lineWidth = 1;
        canvasCtx.beginPath();
        canvasCtx.moveTo(0, HEIGHT / 2);
        canvasCtx.lineTo(WIDTH, HEIGHT / 2);
        canvasCtx.stroke();

        // Continue animation
        waveformAnimationRef.current = requestAnimationFrame(() => drawWaveform(analyserNode, dataArrayBuffer));
    };

    // Recording functions
    const startRecording = async () => {
        try {
            setAudioDeviceError('');
            setIsRecordingLoading(true);

            // Get microphone stream
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: selectedMicrophone ? { deviceId: { exact: selectedMicrophone } } : true
            });

            setAudioStream(stream);

            // Create MediaRecorder
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            recordedChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    recordedChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(recordedChunksRef.current, { type: 'audio/wav' });
                setRecordedAudioBlob(audioBlob);
                const audioUrl = URL.createObjectURL(audioBlob);
                setRecordedAudioUrl(audioUrl);

                // Stop all tracks
                stream.getTracks().forEach(track => track.stop());
                setAudioStream(null);

                // Auto-playback the newly recorded audio
                setTimeout(async () => {
                    try {
                        setIsPlayingRecording(true);

                        // Create audio element with the new URL
                        const audio = new Audio(audioUrl);
                        playbackAudioRef.current = audio;

                        // Set volume
                        audio.volume = playbackVolume / 100;

                        // Set speaker if available
                        if (selectedSpeaker && 'setSinkId' in audio) {
                            try {
                                await (audio as any).setSinkId(selectedSpeaker);
                            } catch (error) {
                                console.warn('Could not set speaker:', error);
                            }
                        }

                        audio.onended = () => {
                            setIsPlayingRecording(false);
                        };

                        audio.onerror = () => {
                            setAudioDeviceError('Failed to play recording. Please check your speakers.');
                            setIsPlayingRecording(false);
                        };

                        await audio.play();
                    } catch (error: any) {
                        console.error('Failed to play recording:', error);
                        setAudioDeviceError(`Playback failed: ${error.message}`);
                        setIsPlayingRecording(false);
                    }
                }, 500); // Small delay to ensure blob is ready
            };

            mediaRecorder.start();
            setIsRecording(true);
            setIsRecordingLoading(false);
            setRecordingDuration(0);

            // Start recording timer
            recordingTimerRef.current = setInterval(() => {
                setRecordingDuration(prev => {
                    const newDuration = prev + 1;
                    // Auto-stop recording after 10 seconds
                    if (newDuration >= 10) {
                        stopRecording();
                    }
                    return newDuration;
                });
            }, 1000);

            // Start audio visualization
            await startAudioVisualization();

        } catch (error: any) {
            console.error('Failed to start recording:', error);
            setIsRecordingLoading(false);
            if (error.name === 'NotAllowedError') {
                setAudioDeviceError(t('audioTest.microphone.permissionDenied'));
            } else if (error.name === 'NotFoundError') {
                setAudioDeviceError(t('audioTest.microphone.notFound'));
            } else {
                setAudioDeviceError(`Recording failed: ${error.message}`);
            }
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            setIsRecordingLoading(false);

            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }

            stopAudioVisualization();
        }
    };

    const playRecording = async () => {
        if (!recordedAudioBlob || !recordedAudioUrl) return;

        try {
            setIsPlayingRecording(true);

            // Create audio element
            const audio = new Audio(recordedAudioUrl);
            playbackAudioRef.current = audio;

            // Set volume
            audio.volume = playbackVolume / 100;

            // Set speaker if available
            if (selectedSpeaker && 'setSinkId' in audio) {
                try {
                    await (audio as any).setSinkId(selectedSpeaker);
                } catch (error) {
                    console.warn('Could not set speaker:', error);
                }
            }

            audio.onended = () => {
                setIsPlayingRecording(false);
            };

            audio.onerror = () => {
                setAudioDeviceError('Failed to play recording. Please check your speakers.');
                setIsPlayingRecording(false);
            };

            await audio.play();

        } catch (error: any) {
            console.error('Failed to play recording:', error);
            setAudioDeviceError(`Playback failed: ${error.message}`);
            setIsPlayingRecording(false);
        }
    };

    const stopPlayback = () => {
        if (playbackAudioRef.current) {
            playbackAudioRef.current.pause();
            playbackAudioRef.current.currentTime = 0;
            setIsPlayingRecording(false);
        }
    };

    const clearRecording = () => {
        if (recordedAudioUrl) {
            URL.revokeObjectURL(recordedAudioUrl);
        }
        setRecordedAudioBlob(null);
        setRecordedAudioUrl('');
        setRecordingDuration(0);
        stopPlayback();
    };


    // Audio visualization functions
    const startAudioVisualization = async () => {
        try {
            setAudioDeviceError('');

            // Get microphone stream
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: selectedMicrophone ? { deviceId: { exact: selectedMicrophone } } : true
            });

            setAudioStream(stream);

            // Create audio context and analyser
            const ctx = new AudioContext();

            // Resume audio context if suspended (required for user interaction)
            if (ctx.state === 'suspended') {
                await ctx.resume();
            }

            const analyserNode = ctx.createAnalyser();
            const source = ctx.createMediaStreamSource(stream);

            analyserNode.fftSize = 256;
            analyserNode.smoothingTimeConstant = 0.8;
            source.connect(analyserNode);

            const bufferLength = analyserNode.frequencyBinCount;
            const dataArrayBuffer = new Uint8Array(bufferLength);

            setAudioContext(ctx);
            setAnalyser(analyserNode);
            setDataArray(dataArrayBuffer);
            setIsListening(true);

            // Start waveform visualization with proper parameters
            drawWaveform(analyserNode, dataArrayBuffer);

        } catch (error: any) {
            console.error('Failed to start audio visualization:', error);
            if (error.name === 'NotAllowedError') {
                setAudioDeviceError(t('audioTest.microphone.permissionDenied'));
            } else if (error.name === 'NotFoundError') {
                setAudioDeviceError(t('audioTest.microphone.notFound'));
            } else {
                setAudioDeviceError(`${t('audioTest.status.failed')}: ${error.message}`);
            }
        }
    };

    const stopAudioVisualization = () => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }

        if (waveformAnimationRef.current) {
            cancelAnimationFrame(waveformAnimationRef.current);
        }

        if (audioStream) {
            audioStream.getTracks().forEach(track => track.stop());
            setAudioStream(null);
        }

        if (audioContext) {
            audioContext.close();
            setAudioContext(null);
        }

        setAnalyser(null);
        setDataArray(null);
        setIsListening(false);
        setAudioLevel(0);

        // Clear canvas
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
            }
        }
    };

    // Speaker testing functionality
    const testSpeaker = async (deviceId?: string) => {
        try {
            setAudioDeviceError('');
            setIsPlayingTestAudio(true);

            console.log('Starting speaker test...');

            // Create audio context
            const ctx = new AudioContext();
            console.log('Audio context created, state:', ctx.state);

            // Resume audio context if suspended (required for user interaction)
            if (ctx.state === 'suspended') {
                console.log('Resuming suspended audio context...');
                await ctx.resume();
                console.log('Audio context resumed, state:', ctx.state);
            }

            // Create a test tone
            const oscillator = ctx.createOscillator();
            const gainNode = ctx.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(ctx.destination);

            // Create a pleasant test tone
            oscillator.frequency.setValueAtTime(440, ctx.currentTime); // A4 note
            oscillator.type = 'sine';

            // Set initial volume
            gainNode.gain.setValueAtTime(0.1, ctx.currentTime);

            console.log('Starting oscillator...');
            oscillator.start(ctx.currentTime);
            oscillator.stop(ctx.currentTime + 2);

            // Handle oscillator end
            oscillator.onended = () => {
                console.log(t('audioTest.messages.oscillatorEnded'));
                try {
                    ctx.close();
                } catch (e) {
                    console.warn('Error closing audio context:', e);
                }
                setIsPlayingTestAudio(false);
            };

        } catch (error: any) {
            console.error('Failed to test speaker:', error);
            setAudioDeviceError(`Failed to test speaker: ${error.message}`);
            setIsPlayingTestAudio(false);
        }
    };

    // Simple immediate playback functionality with fallback
    const playTestTone = async () => {
        try {
            setAudioDeviceError('');
            setIsPlayingTestAudio(true);

            console.log('Playing simple test tone...');

            // Try Web Audio API first
            try {
                const ctx = new AudioContext();

                // Resume audio context if suspended
                if (ctx.state === 'suspended') {
                    await ctx.resume();
                }

                // Create a simple test tone
                const oscillator = ctx.createOscillator();
                const gainNode = ctx.createGain();

                oscillator.connect(gainNode);
                gainNode.connect(ctx.destination);

                // Set frequency and type
                oscillator.frequency.setValueAtTime(440, ctx.currentTime); // A4 note
                oscillator.type = 'sine';

                // Set volume
                gainNode.gain.setValueAtTime(0.1, ctx.currentTime);

                // Start and stop
                oscillator.start();
                oscillator.stop(ctx.currentTime + 1); // Play for 1 second

                // Clean up when done
                setTimeout(() => {
                    try {
                        ctx.close();
                    } catch (e) {
                        console.warn('Error closing audio context:', e);
                    }
                    setIsPlayingTestAudio(false);
                }, 1100); // Slightly longer than the tone

            } catch (webAudioError) {
                console.warn('Web Audio API failed, trying HTML5 Audio fallback:', webAudioError);

                // Fallback to HTML5 Audio
                const audio = new Audio();

                // Create a data URL for a simple tone (1 second of 440Hz sine wave)
                const sampleRate = 44100;
                const duration = 1;
                const frequency = 440;
                const samples = sampleRate * duration;
                const buffer = new ArrayBuffer(44 + samples * 2);
                const view = new DataView(buffer);

                // WAV header
                const writeString = (offset: number, string: string) => {
                    for (let i = 0; i < string.length; i++) {
                        view.setUint8(offset + i, string.charCodeAt(i));
                    }
                };

                writeString(0, 'RIFF');
                view.setUint32(4, 36 + samples * 2, true);
                writeString(8, 'WAVE');
                writeString(12, 'fmt ');
                view.setUint32(16, 16, true);
                view.setUint16(20, 1, true);
                view.setUint16(22, 1, true);
                view.setUint32(24, sampleRate, true);
                view.setUint32(28, sampleRate * 2, true);
                view.setUint16(32, 2, true);
                view.setUint16(34, 16, true);
                writeString(36, 'data');
                view.setUint32(40, samples * 2, true);

                // Generate sine wave
                for (let i = 0; i < samples; i++) {
                    const sample = Math.sin(2 * Math.PI * frequency * i / sampleRate) * 0.1;
                    view.setInt16(44 + i * 2, sample * 32767, true);
                }

                const blob = new Blob([buffer], { type: 'audio/wav' });
                const url = URL.createObjectURL(blob);

                audio.src = url;
                audio.volume = 0.1;

                audio.onended = () => {
                    URL.revokeObjectURL(url);
                    setIsPlayingTestAudio(false);
                };

                audio.onerror = () => {
                    URL.revokeObjectURL(url);
                    setAudioDeviceError('Failed to play test tone. Please check your speakers.');
                    setIsPlayingTestAudio(false);
                };

                await audio.play();
            }

        } catch (error: any) {
            console.error('Failed to play test tone:', error);
            setAudioDeviceError(`Failed to play test tone: ${error.message}`);
            setIsPlayingTestAudio(false);
        }
    };

    const playTestSequence = async () => {
        try {
            setAudioDeviceError('');
            setIsPlayingTestAudio(true);

            console.log('Starting test sequence...');

            const ctx = new AudioContext();
            console.log('Audio context created for sequence, state:', ctx.state);

            // Resume audio context if suspended (required for user interaction)
            if (ctx.state === 'suspended') {
                console.log('Resuming suspended audio context for sequence...');
                await ctx.resume();
                console.log('Audio context resumed for sequence, state:', ctx.state);
            }

            // Play a sequence of tones to test audio
            const frequencies = [261.63, 329.63, 392.00, 523.25]; // C, E, G, C (C major chord)
            let completedOscillators = 0;

            for (let i = 0; i < frequencies.length; i++) {
                const oscillator = ctx.createOscillator();
                const gainNode = ctx.createGain();

                oscillator.connect(gainNode);
                gainNode.connect(ctx.destination);

                oscillator.frequency.setValueAtTime(frequencies[i], ctx.currentTime);
                oscillator.type = 'sine';

                const startTime = ctx.currentTime + (i * 0.5);
                const endTime = startTime + 0.4;

                gainNode.gain.setValueAtTime(0, startTime);
                gainNode.gain.linearRampToValueAtTime(0.1, startTime + 0.05);
                gainNode.gain.linearRampToValueAtTime(0, endTime);

                oscillator.onended = () => {
                    completedOscillators++;
                    console.log(`Oscillator ${i + 1} ended. Completed: ${completedOscillators}/${frequencies.length}`);

                    if (completedOscillators === frequencies.length) {
                        console.log(t('audioTest.messages.allOscillatorsCompleted'));
                        try {
                            ctx.close();
                        } catch (e) {
                            console.warn('Error closing audio context:', e);
                        }
                        setIsPlayingTestAudio(false);
                    }
                };

                console.log(`Starting oscillator ${i + 1} at ${startTime}s`);
                oscillator.start(startTime);
                oscillator.stop(endTime);
            }

        } catch (error: any) {
            console.error('Failed to play test sequence:', error);
            setAudioDeviceError(`Failed to play test audio: ${error.message}`);
            setIsPlayingTestAudio(false);
        }
    };

    // Test audio device functionality
    const testAudioDevice = async (deviceId: string) => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { deviceId: { exact: deviceId } }
            });

            // Test if we can get audio data
            const audioContext = new AudioContext();
            const source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            source.connect(analyser);

            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            analyser.getByteFrequencyData(dataArray);

            // Stop the test stream
            stream.getTracks().forEach(track => track.stop());
            audioContext.close();

            return true;
        } catch (error) {
            console.error('Audio device test failed:', error);
            return false;
        }
    };


    // Comprehensive audio test with visual feedback
    const runAudioTest = async () => {
        setIsTestingAudio(true);
        setAudioDeviceError('');

        try {
            // Step 1: Check permissions
            await navigator.mediaDevices.getUserMedia({ audio: true });

            // Step 2: Test microphone
            if (selectedMicrophone) {
                const micTest = await testAudioDevice(selectedMicrophone);
                if (!micTest) {
                    setAudioDeviceError(t('audioTest.status.failed'));
                    return;
                }
            }

            // Step 3: Test audio output (speaker)
            try {
                const audioContext = new AudioContext();

                // Resume audio context if suspended
                if (audioContext.state === 'suspended') {
                    await audioContext.resume();
                }

                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();

                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);

                oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
                gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);

                oscillator.start();

                // Play test tone for 1 second
                setTimeout(() => {
                    try {
                        oscillator.stop();
                        audioContext.close();
                    } catch (e) {
                        console.warn('Error stopping oscillator:', e);
                    }
                    message.success(t('audioTest.status.completed'));
                }, 1000);

            } catch (speakerError) {
                console.warn('Speaker test failed:', speakerError);
                message.warning(t('audioTest.status.failed'));
            }

        } catch (error: any) {
            console.error('Audio test failed:', error);
            if (error.name === 'NotAllowedError') {
                setAudioDeviceError(t('audioTest.microphone.permissionDeniedBrowser'));
            } else if (error.name === 'NotFoundError') {
                setAudioDeviceError(t('audioTest.microphone.notFoundRetry'));
            } else {
                setAudioDeviceError(`Audio test failed: ${error.message}`);
            }
        } finally {
            setIsTestingAudio(false);
        }
    };

    // Device change handlers with memory
    const handleMicrophoneChange = (deviceId: string) => {
        if (onMicrophoneChange) {
            onMicrophoneChange(deviceId);
        }
        if (selectedSpeaker) {
            saveDeviceSettings(deviceId, selectedSpeaker);
        }
    };

    const handleSpeakerChange = (deviceId: string) => {
        if (onSpeakerChange) {
            onSpeakerChange(deviceId);
        }
        if (selectedMicrophone) {
            saveDeviceSettings(selectedMicrophone, deviceId);
        }
    };

    const handleClose = () => {
        stopAudioVisualization();
        stopRecording();
        stopPlayback();
        clearRecording();
        setAudioDeviceError('');
        setIsRecordingLoading(false);
        onClose();
    };

    // Clear invalid device settings when devices change
    useEffect(() => {
        if (availableMicrophones.length > 0 && availableSpeakers.length > 0) {
            clearInvalidDeviceSettings();
        }
    }, [availableMicrophones, availableSpeakers]);

    // Load device settings on mount and when devices change
    useEffect(() => {
        if (open && availableMicrophones.length > 0 && availableSpeakers.length > 0) {
            loadDeviceSettings();
        }
    }, [open, availableMicrophones, availableSpeakers]);

    // Save settings when devices change
    useEffect(() => {
        if (selectedMicrophone && selectedSpeaker) {
            saveDeviceSettings(selectedMicrophone, selectedSpeaker);
        }
    }, [selectedMicrophone, selectedSpeaker]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopAudioVisualization();
            stopRecording();
            stopPlayback();
            clearRecording();
            if (testAudioRef.current) {
                testAudioRef.current.pause();
            }
        };
    }, []);

    return (
        <Modal
            title={
                <div style={{ textAlign: 'center', fontSize: 20, fontWeight: 600, color: '#1890ff' }}>
                    {t('audioTest.title')}
                </div>
            }
            open={open}
            onCancel={handleClose}
            footer={null}
            width={600}
            style={{ top: 20 }}
            styles={{ 
                body: { 
                    padding: '24px 32px',
                    backgroundColor: '#1f1f1f',
                    color: '#ffffff'
                },
                header: {
                    backgroundColor: '#1f1f1f',
                    borderBottom: '1px solid #333333'
                },
                content: {
                    backgroundColor: '#1f1f1f'
                }
            }}
        >
            <div style={{ textAlign: 'center' }}>
                {/* Audio Device Error Display */}
                {audioDeviceError && (
                    <Alert
                        message={t('agora.audioDeviceIssue')}
                        description={audioDeviceError}
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                        action={
                            onRefreshDevices && (
                                <Button size="small" onClick={onRefreshDevices}>
                                    {t('agora.retryDetection')}
                                </Button>
                            )
                        }
                    />
                )}

                {/* Device Selection Section */}
                <div style={{ marginBottom: 24 }}>
                    {/* Microphone Selection with Live Monitoring */}
                    {availableMicrophones.length > 0 && onMicrophoneChange && (
                        <div style={{ marginBottom: 20, textAlign: 'left' }}>
                            <Text strong style={{ display: 'block', marginBottom: 12, fontSize: 16, color: '#1890ff' }}>
                                {t('audioTest.microphone.title')}
                            </Text>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                border: '2px solid #333333',
                                borderRadius: 12,
                                padding: '8px 16px',
                                backgroundColor: '#2a2a2a',
                                transition: 'all 0.3s ease'
                            }}>
                                <AudioOutlined style={{
                                    fontSize: 18,
                                    color: '#1890ff',
                                    marginRight: 12,
                                    minWidth: 20
                                }} />
                                <Select
                                    value={selectedMicrophone || availableMicrophones[0]?.deviceId}
                                    onChange={handleMicrophoneChange}
                                    style={{
                                        flex: 1,
                                        border: 'none',
                                        backgroundColor: 'transparent',
                                        color: '#ffffff'
                                    }}
                                    dropdownStyle={{
                                        borderRadius: 12,
                                        border: '1px solid #333333',
                                        backgroundColor: '#2a2a2a',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                                    }}
                                    suffixIcon={null}
                                >
                                    {availableMicrophones.map((mic) => (
                                        <Select.Option key={mic.deviceId} value={mic.deviceId}>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <AudioOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                                                {mic.label || `Microphone ${mic.deviceId.slice(0, 8)}`}
                                            </div>
                                        </Select.Option>
                                    ))}
                                </Select>
                                <SettingOutlined style={{
                                    fontSize: 16,
                                    color: '#999',
                                    cursor: 'pointer',
                                    padding: 4,
                                    borderRadius: 4
                                }} />
                            </div>
                            
                            {/* Record-Playback Controls */}
                            <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Button
                                    type={isRecording ? 'primary' : 'default'}
                                    onClick={isRecording ? stopRecording : startRecording}
                                    disabled={isRecordingLoading}
                                    loading={isRecordingLoading}
                                    style={{
                                        minWidth: 120,
                                        height: 36,
                                        borderRadius: 18,
                                        fontSize: 14,
                                        fontWeight: 500,
                                        border: isRecording ? 'none' : '2px solid #333333',
                                        backgroundColor: isRecording ? '#ff4d4f' : isRecordingLoading ? '#2a2a2a' : '#2a2a2a',
                                        color: isRecording ? 'white' : isRecordingLoading ? '#999' : '#1890ff'
                                    }}
                                    size="small"
                                    icon={isRecordingLoading ? <LoadingOutlined /> : isRecording ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                                >
                                    {isRecordingLoading ? t('audioTest.microphone.settingUp') : isRecording ? t('audioTest.microphone.stopRecording') : t('audioTest.microphone.record')}
                                </Button>

                                {/* Audio Visualization - positioned next to the button */}
                                {(isRecording || isListening) && (
                                    <div style={{
                                        width: 200,
                                        height: 60,
                                        backgroundColor: '#1a1a1a',
                                        borderRadius: 12,
                                        border: '2px solid #333333',
                                        overflow: 'hidden',
                                        position: 'relative',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <canvas
                                            ref={canvasRef}
                                            width={200}
                                            height={60}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'block'
                                            }}
                                        />
                                        {/* Audio level overlay */}
                                        <div style={{
                                            position: 'absolute',
                                            top: 4,
                                            right: 4,
                                            backgroundColor: 'rgba(24, 144, 255, 0.8)',
                                            color: 'white',
                                            padding: '2px 6px',
                                            borderRadius: 4,
                                            fontSize: 10,
                                            fontWeight: 500
                                        }}>
                                            {Math.round(audioLevel * 100)}%
                                        </div>
                                    </div>
                                )}

                                {recordedAudioBlob && (
                                    <Button
                                        onClick={clearRecording}
                                        style={{
                                            minWidth: 80,
                                            height: 36,
                                            borderRadius: 18,
                                            fontSize: 14,
                                            fontWeight: 500,
                                            border: '2px solid #333333',
                                            backgroundColor: '#2a2a2a',
                                            color: '#cccccc'
                                        }}
                                        size="small"
                                    >
                                        Clear
                                    </Button>
                                )}

                                {/* Recording Duration and Playback Status */}
                                {isRecording && (
                                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: 8, gap: 8 }}>
                                        <Text style={{ 
                                            fontSize: 12, 
                                            color: recordingDuration >= 8 ? '#ff4d4f' : '#cccccc', 
                                            fontWeight: recordingDuration >= 8 ? 500 : 'normal'
                                        }}>
                                            {recordingDuration}s / 10s
                                        </Text>
                                        <div style={{
                                            width: 60,
                                            height: 4,
                                            backgroundColor: '#333333',
                                            borderRadius: 2,
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                width: `${(recordingDuration / 10) * 100}%`,
                                                height: '100%',
                                                backgroundColor: recordingDuration >= 8 ? '#ff4d4f' : '#1890ff',
                                                transition: 'all 0.3s ease'
                                            }} />
                                        </div>
                                    </div>
                                )}
                                {isPlayingRecording && (
                                    <Text style={{ fontSize: 12, color: '#52c41a', marginLeft: 8 }}>
                                        Playing back recording...
                                    </Text>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Speaker Selection with Test Audio */}
                    {availableSpeakers.length > 0 && onSpeakerChange && (
                        <div style={{ marginBottom: 20, textAlign: 'left' }}>
                            <Text strong style={{ display: 'block', marginBottom: 12, fontSize: 16, color: '#1890ff' }}>
                                {t('audioTest.speaker.title')}
                            </Text>
                            {isIPhone() ? (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: '2px solid #333333',
                                    borderRadius: 12,
                                    padding: '8px 16px',
                                    backgroundColor: '#2a2a2a',
                                    color: '#cccccc'
                                }}>
                                    <VideoCameraOutlined style={{
                                        fontSize: 18,
                                        color: '#1890ff',
                                        marginRight: 12,
                                        minWidth: 20
                                    }} />
                                    <div style={{ flex: 1, fontSize: 14 }}>
                                        {availableSpeakers.length > 0 ? availableSpeakers[0].label : t('audioTest.speaker.defaultSpeaker')}
                                        <div style={{ fontSize: 12, color: '#cccccc', marginTop: 2 }}>
                                            iOS handles speaker routing automatically
                                        </div>
                                    </div>
                                    <SettingOutlined style={{
                                        fontSize: 16,
                                        color: '#999',
                                        cursor: 'pointer',
                                        padding: 4,
                                        borderRadius: 4
                                    }} />
                                </div>
                            ) : (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: '2px solid #333333',
                                    borderRadius: 12,
                                    padding: '8px 16px',
                                    backgroundColor: '#2a2a2a',
                                    transition: 'all 0.3s ease'
                                }}>
                                    <VideoCameraOutlined style={{
                                        fontSize: 18,
                                        color: '#1890ff',
                                        marginRight: 12,
                                        minWidth: 20
                                    }} />
                                    <Select
                                        value={selectedSpeaker || availableSpeakers[0]?.deviceId}
                                        onChange={handleSpeakerChange}
                                        style={{
                                            flex: 1,
                                            border: 'none',
                                            backgroundColor: 'transparent',
                                            color: '#ffffff'
                                        }}
                                        dropdownStyle={{
                                            borderRadius: 12,
                                            border: '1px solid #333333',
                                            backgroundColor: '#2a2a2a',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                                        }}
                                        suffixIcon={null}
                                    >
                                        {availableSpeakers.map((speaker) => (
                                            <Select.Option key={speaker.deviceId} value={speaker.deviceId}>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <VideoCameraOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                                                    {speaker.label || `Speaker ${speaker.deviceId.slice(0, 8)}`}
                                                </div>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                    <SettingOutlined style={{
                                        fontSize: 16,
                                        color: '#999',
                                        cursor: 'pointer',
                                        padding: 4,
                                        borderRadius: 4
                                    }} />
                                </div>
                            )}
                            
                            {/* Test Audio Buttons */}
                            <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Button
                                    type={isPlayingTestAudio ? 'primary' : 'default'}
                                    onClick={playTestSequence}
                                    disabled={isPlayingTestAudio}
                                    style={{
                                        minWidth: 120,
                                        height: 36,
                                        borderRadius: 18,
                                        fontSize: 14,
                                        fontWeight: 500,
                                        border: isPlayingTestAudio ? 'none' : '2px solid #333333',
                                        backgroundColor: isPlayingTestAudio ? '#1890ff' : '#2a2a2a',
                                        color: isPlayingTestAudio ? 'white' : '#1890ff'
                                    }}
                                    size="small"
                                    icon={<VideoCameraOutlined />}
                                >
                                    {isPlayingTestAudio ? t('audioTest.speaker.playing') : t('audioTest.speaker.testTone')}
                                </Button>

                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default AudioTestModal;
